'use client'

import * as React from 'react'
import dynamic from 'next/dynamic'
import { cn } from '../../../lib/utils'
import { type PageProps } from '../PageV2'
import { SideBarStructure } from './SideBar/navigation'
import { type SidebarStructureProps } from './SideBar/navigation'
import { List, type ListProps } from '../List'
import { Input } from '../Input'
import { ToolMarketplace, type ToolMarketplaceProps } from '../ToolMarketplace'
import { ThreadsSidebar, type ThreadsSidebarProps } from '../ThreadsSidebar'
import { LEFT_SIDE_NAV_WIDTH } from '../Layout/constants'

const PageV2 = dynamic(() => import('../PageV2').then((mod) => mod.PageV2), {
  ssr: false,
})

export type StyleProps = {
  className?: string
}

export type PageV2Component = {
  component: 'PageV2'
  props: PageProps
} & StyleProps

export type SidebarComponent = {
  component: 'Sidebar'
  props: SidebarStructureProps
} & StyleProps

export type ListComponent = {
  component: 'List'
  props: ListProps
} & StyleProps

export type ToolMarketplaceComponent = {
  component: 'ToolMarketplace'
  props: ToolMarketplaceProps
} & StyleProps

export type CustomComponent = {
  component: 'Custom'
  props: {
    children: React.ReactNode
  }
} & StyleProps

export type ThreadsSidebarComponent = {
  component: 'ThreadsSidebar'
  props: ThreadsSidebarProps
} & StyleProps

export type SideNavigationComponent =
  | PageV2Component
  | SidebarComponent
  | ListComponent
  | ToolMarketplaceComponent
  | CustomComponent
  | ThreadsSidebarComponent

export type SideNavigationV2Props<T extends SideNavigationComponent> = {
  side: 'left' | 'right'
  isOpen: boolean
  width?: string
  onSidebarOpenChange?: (open: boolean) => void | Promise<void>
  component: T
  className?: string
}

const SideNavigationV2: React.ForwardRefExoticComponent<
  SideNavigationV2Props<SideNavigationComponent> &
    React.RefAttributes<HTMLDivElement>
> = React.forwardRef(
  <T extends SideNavigationComponent>(
    { component, className, side, width, isOpen }: SideNavigationV2Props<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const isSidebar = component.component === 'Sidebar'
    const scrollContainerRef = React.useRef<HTMLDivElement>(null)

    return (
      <div ref={ref} className={cn('flex h-full w-full', className)}>
        <div
          className={cn(
            'h-full w-full transition-transform duration-300 ease-in-out relative',
            component.className,
            {
              'ml-auto': side === 'right',
            },
          )}
        >
          {component.component === 'Sidebar' && (
            <div className="absolute inset-0">
              <SideBarStructure
                {...{
                  ...component.props,
                  side,
                  width: width ?? LEFT_SIDE_NAV_WIDTH,
                }}
                side={side}
              />
            </div>
          )}
          {component.component === 'PageV2' && (
            <div
              ref={scrollContainerRef}
              className="h-full overflow-y-auto p-4"
            >
              <PageV2 {...component.props} />
            </div>
          )}
          {component.component === 'List' && (
            <div className="flex flex-col h-full">
              <div
                ref={scrollContainerRef}
                className="flex flex-col grow overflow-y-auto"
              >
                <div className="sticky top-0 bg-background p-2 py-4 z-10">
                  {component.props.search && (
                    <Input {...component.props.search} leftIcon="Search" />
                  )}
                </div>
                <div className="p-2">
                  <List
                    {...component.props}
                    infiniteScroll={
                      component.props.infiniteScroll
                        ? {
                            ...component.props.infiniteScroll,
                            scrollContainer: scrollContainerRef.current,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          )}
          {component.component === 'ToolMarketplace' && (
            <div className="h-full overflow-hidden">
              <ToolMarketplace {...component.props} />
            </div>
          )}
          {component.component === 'Custom' && (
            <div className="h-full overflow-hidden">
              {component.props.children}
            </div>
          )}
          {component.component === 'ThreadsSidebar' && (
            <div className="h-full overflow-hidden">
              <ThreadsSidebar {...component.props} />
            </div>
          )}
        </div>
      </div>
    )
  },
)

SideNavigationV2.displayName = 'SideNavigationV2'

export { SideNavigationV2 }
